import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class IndexedDbService {
  private _db: IDBDatabase | null = null;
  private _dbInitPromise: Promise<IDBDatabase> | null = null;

  constructor() {}

  /**
   * Initialize the IndexedDB database with error handling
   */
  initDatabase(dbName: string, dbVersion: number, upgradeCallback?: (db: IDBDatabase) => void): Promise<IDBDatabase> {
    // Store the promise so we can reuse it
    if (!this._dbInitPromise) {
      this._dbInitPromise = new Promise<IDBDatabase>((resolve, reject) => {
        if (!window.indexedDB) {
          console.error('IndexedDB is not supported by this browser');
          reject(new Error('IndexedDB not supported'));
          return;
        }

        const request = indexedDB.open(dbName, dbVersion);

        request.onerror = () => {
          console.error('Database error:', request.error);
          this._dbInitPromise = null; // Reset so we can try again
          reject(request.error || new Error('Unknown database error'));
        };

        request.onsuccess = () => {
          this._db = request.result;

          this._db.onerror = event => console.error('Database error:', event);
          this._db.onclose = () => {
            console.warn('Database connection unexpectedly closed');
            this._db = null;
            this._dbInitPromise = null;
          };
          this._db.onversionchange = () => {
            this._db?.close();
            this._db = null;
            this._dbInitPromise = null;
            console.warn('Database version changed, connection closed');
          };

          resolve(this._db);
        };

        request.onupgradeneeded = event => {
          try {
            const db = request.result;
            if (upgradeCallback) {
              upgradeCallback(db);
            }
          } catch (error) {
            console.error('Upgrade callback error:', error);
            // Abort the upgrade transaction to avoid a broken schema
            (event.target as IDBOpenDBRequest).transaction?.abort();
          }
        };

        request.onblocked = () => {
          console.warn('Database upgrade blocked by another connection');
          alert('Please close other tabs with this application open');
          this._dbInitPromise = null;
          reject(new Error('Database upgrade blocked'));
        };
      });
    }

    return this._dbInitPromise;
  }

  /**
   * Perform a transaction on the database
   */
  async performTransaction<T>(
    storeName: string,
    mode: IDBTransactionMode,
    operation: (store: IDBObjectStore) => IDBRequest<T>
  ): Promise<T> {
    // Ensure database is initialized before proceeding
    if (!this._db) {
      throw new Error(
        'IndexedDB not initialized. Please call initDatabase(dbName, dbVersion) before performing transactions.'
      );
    }

    return new Promise<T>((resolve, reject) => {
      try {
        const transaction = this._db!.transaction(storeName, mode);
        const store = transaction.objectStore(storeName);

        const request = operation(store);

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => {
          console.error('Request error:', request.error);
          reject(request.error);
        };

        transaction.onerror = () => {
          console.error('Transaction error:', transaction.error);
          reject(transaction.error);
        };
        transaction.onabort = () => {
          console.error('Transaction aborted:', transaction.error);
          reject(transaction.error || new Error('Transaction aborted'));
        };
        // Add an oncomplete event handler to ensure transaction completes
        transaction.oncomplete = () => {
          // If request hasn't resolved yet, it might be a bug
          // This helps with debugging
          console.debug('Transaction completed');
        };
      } catch (error) {
        console.error('Error in performTransaction:', error);
        reject(error);
      }
    });
  }

  /**
   * Check if the database is initialized
   */
  isInitialized(): boolean {
    return this._db !== null;
  }

  /**
   * Delete the database
   */
  async deleteDatabase(dbName: string): Promise<void> {
    if (this._db) {
      this._db.close();
      this._db = null;
      this._dbInitPromise = null;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.deleteDatabase(dbName);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
      request.onblocked = () => {
        console.warn('Database deletion blocked');
        alert('Please close all other tabs with this application open');
        reject(new Error('Database deletion blocked'));
      };
    });
  }
}
